import React, { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr + 'T12:00:00')?.toLocaleDateString('es-MX', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatCreatedAt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso)?.toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Row = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex gap-2 py-1.5 border-b border-white/10 last:border-0">
      <span className="text-white/60 text-xs w-32 flex-shrink-0 font-medium uppercase tracking-wide">{label}</span>
      <span className="text-white text-sm font-medium break-words">{value}</span>
    </div>
  );
};

const AppointmentAlertModal = ({ data, onClose }) => {
  const isNueva = data?.tipo !== 'reagendada';
  const titleText = isNueva ? 'NUEVA CITA GENERADA' : 'CITA REAGENDADA';
  const titleColor = isNueva ? 'text-emerald-300' : 'text-amber-300';
  const borderColor = isNueva ? 'border-emerald-400/60' : 'border-amber-400/60';
  const badgeBg = isNueva ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40' : 'bg-amber-500/20 text-amber-300 border-amber-400/40';
  const glowClass = isNueva ? 'shadow-emerald-500/20' : 'shadow-amber-500/20';

  // Log notification on mount
  const loggedRef = useRef(false);
  useEffect(() => {
    if (loggedRef?.current) return;
    loggedRef.current = true;
    const logNotification = async () => {
      try {
        await supabase?.from('notifications')?.insert({
          type: isNueva ? 'nueva_cita' : 'cita_reagendada',
          title: titleText,
          message: `${data?.origen || 'Sistema'} — ${data?.cliente || '—'} | ${data?.servicio || '—'} | ${data?.fecha || '—'} ${data?.hora || ''}`,
          is_read: false,
          created_at: data?.createdAt || new Date()?.toISOString(),
        });
      } catch (_) { /* non-blocking */ }
    };
    logNotification();
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
    >
      {/* Pulsing ring */}
      <div
        className={`absolute inset-0 pointer-events-none ${isNueva ? 'animate-ping-slow-green' : 'animate-ping-slow-amber'}`}
        style={{ border: isNueva ? '2px solid rgba(52,211,153,0.15)' : '2px solid rgba(251,191,36,0.15)', borderRadius: 0 }}
      />

      <div
        className={`relative w-full max-w-lg rounded-2xl border ${borderColor} shadow-2xl ${glowClass} overflow-hidden`}
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}
      >
        {/* Animated top bar */}
        <div
          className={`h-1 w-full ${isNueva ? 'bg-emerald-400' : 'bg-amber-400'}`}
          style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
        />

        {/* Header */}
        <div className="px-6 pt-5 pb-4 text-center">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold tracking-widest mb-3 ${badgeBg}`}>
            <span
              className={`w-2 h-2 rounded-full ${isNueva ? 'bg-emerald-400' : 'bg-amber-400'}`}
              style={{ animation: 'pulse 1s ease-in-out infinite' }}
            />
            {data?.origen || 'Sistema'}
          </div>
          <h2
            className={`text-2xl font-black tracking-tight ${titleColor}`}
            style={{ animation: 'pulse 2s ease-in-out infinite' }}
          >
            {titleText}
          </h2>
        </div>

        {/* Data rows */}
        <div className="px-6 pb-2 space-y-0">
          <Row label="Cliente" value={data?.cliente} />
          <Row label="Teléfono" value={data?.telefono} />
          <Row label="Vehículo" value={data?.vehiculo} />
          <Row label="Placa" value={data?.placa} />
          <Row label="Servicio" value={data?.servicio} />
          <Row label="Fecha" value={formatDate(data?.fecha)} />
          <Row label="Hora" value={data?.hora} />
          <Row label="Notas" value={data?.notas} />
          <Row label="Origen" value={data?.origen} />
          <Row label="Generado por" value={data?.usuario} />
          <Row label="Creado el" value={formatCreatedAt(data?.createdAt)} />
        </div>

        {/* Footer */}
        <div className="px-6 py-5 flex justify-center">
          <button
            onClick={onClose}
            className={`px-8 py-3 rounded-xl font-bold text-sm tracking-wide transition-all active:scale-95 ${
              isNueva
                ? 'bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/30'
                : 'bg-amber-500 hover:bg-amber-400 text-white shadow-lg shadow-amber-500/30'
            }`}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentAlertModal;
